import React, { useState, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet";
import { useNavigate, useSearchParams } from "react-router-dom";

import { zoneService } from "/app/src/services";
import { useTranslation } from "react-i18next";
import { Zone as ZoneType } from "/app/src/models";
import { createColumnHelper } from "@tanstack/react-table";
import Table from "/app/src/components/generic/tables/table";
import { useSortUpgrade } from "/app/src/hooks/useSortUpgrade";
import usePaginatedData from "/app/src/hooks/usePaginatedData";
import useQueryParams from "/app/src/hooks/useQueryParams";
import Breadcrumbs from "/app/src/components/generic/components/breadcrumbs";
import HeroSearch from "/app/src/components/generic/components/heroSearch";
import {
  Button,
  Checkbox,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@heroui/react";
import useSetting from "/app/src/components/settings/setting";
import {
  isColumnSelected,
  toggleExplorerColumn,
  buildExplorerColumns,
} from "/app/src/components/explorer/helpers/columns";
import { useAuthState } from "/app/src/contexts/authentication";

/**
 * Renders the Zones component displaying zones data.
 *
 * @returns {JSX.Element} The rendered Zones component.
 */
export default function Zones() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [sort, setSort] = useSortUpgrade([]);
  const [searchParams] = useSearchParams();

  const [searchString, setSearchString] = useState(
    searchParams.get("search") || "",
  );
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1") - 1,
  );
  const [pageSize, setPageSize] = useState(
    parseInt(searchParams.get("pageSize") || "25"),
  );

  const { user } = useAuthState();
  const columnHelper = createColumnHelper<ZoneType>();

  const availableColumns = [
    { id: "description", label: t("translation:description") },
    { id: "pickType", label: t("translation:pick_type") },
    { id: "putType", label: t("translation:put_type") },
  ];

  const { settingValue, createUpdateSetting } = useSetting({
    name: "explorerColumns",
    userId: user?.id,
  });

  const isColumnChecked = useCallback(
    (id: string) => isColumnSelected(settingValue, "zone", id),
    [settingValue],
  );

  const toggleColumnSelection = useCallback(
    async (id: string) =>
      toggleExplorerColumn(
        settingValue,
        createUpdateSetting,
        "zone",
        id,
        user?.id,
      ),
    [settingValue, createUpdateSetting, user?.id],
  );

  const columns = useMemo(() => {
    /**
     * Creates a column definition based on the given id.
     * @param {string} id - The identifier of the column to create.
     * @returns {Object|null} The column definition object or null if the id is not recognized.
     */
    const makeColumn = (id: string) => {
      switch (id) {
        case "name":
          return columnHelper.accessor("name", {
            id: "name",
            header: t("translation:name"),
            cell: (info) => info.getValue(),
          });
        case "description":
          return columnHelper.accessor("description", {
            id: "description",
            header: t("translation:description"),
            cell: (info) => info.getValue(),
          });
        case "pickType":
          return columnHelper.accessor("pickTypeDescription", {
            id: "pickType",
            header: t("translation:pick_type"),
            cell: (info) => info.getValue(),
          });
        case "putType":
          return columnHelper.accessor("putTypeDescription", {
            id: "putType",
            header: t("translation:put_type"),
            cell: (info) => info.getValue(),
          });
        default:
          return null;
      }
    };

    return buildExplorerColumns(settingValue, "zone", makeColumn, "name");
  }, [columnHelper, settingValue, t]);

  const gotToZone = useCallback(
    (id: number) => {
      navigate(`/explorer/zones/${id}`);
    },
    [navigate],
  );

  const {
    data: zones,
    count: zonesCount,
    isFetching,
  } = usePaginatedData<ZoneType>({
    queryKey: ["zones"],
    searchString,
    sort,
    page,
    pageSize,
    service: zoneService,
  });

  const { updatePage, updatePageSize } = useQueryParams({
    count: zonesCount,
    isFetching,
    page,
    setPage,
    pageSize,
    setPageSize,
  });

  const createToggleHandler = useCallback(
    (id: string) => () => {
      toggleColumnSelection(id);
    },
    [toggleColumnSelection],
  );

  return (
    <div className="explorer">
      <Helmet>
        <title>{t("translation:zones")} - ItemPath</title>
      </Helmet>
      <Breadcrumbs
        items={[
          { label: "Explorer", to: "/explorer" },
          { label: t("translation:orders") },
        ]}
      />

      <div className="mt-12 mb-4 flex justify-between">
        <div className="flex justify-between gap-2">
          <HeroSearch
            className="w-[300px]"
            setSearchString={setSearchString}
            allowClear
            box={false}
          />
        </div>
        <Popover placement="bottom" radius="md" aria-label="Customize columns">
          <PopoverTrigger>
            <Button size="md" radius="md" color="primary">
              {t("translation:customize_columns")}
            </Button>
          </PopoverTrigger>
          <PopoverContent>
            <div className="w-[300px] p-4">
              <h3 className="text-[16px]">
                {t("translation:available_columns")}
              </h3>
              <div className="mt-2 flex flex-col gap-2">
                {availableColumns.map((column) => (
                  <Checkbox
                    size="sm"
                    key={column.id}
                    defaultSelected={isColumnChecked(column.id)}
                    onChange={createToggleHandler(column.id)}
                  >
                    {column.label}
                  </Checkbox>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      <Table
        loading={isFetching}
        rows={zones}
        tableColumns={columns}
        length={zonesCount}
        sort={sort}
        setSort={setSort}
        paginationEnabled={{
          currentPage: page,
          pageSize,
          setPage: updatePage,
          setPageSize: updatePageSize,
        }}
        rowClicked={gotToZone}
        emptyText={`${t("translation:no")} ${t("translation:zones")} ${t(
          "translation:found",
        )}`}
      />
    </div>
  );
}
